'use client';

import React, { useState, useMemo } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Box, Typography, Button, IconButton, Switch, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import { useAtms, useUpdateAtmAsset, useCreateAtmAsset } from '@/hooks/api/useAtms';
import { useLocations } from '@/hooks/useLocation';
import { orderBy } from 'lodash';
import { Download, Delete, Restore, Edit } from '@mui/icons-material';
import TitleBar from '../layout/TitleBar';
import { useNavigate } from 'react-router-dom';
import { useAuthData } from '@/hooks/getAuthData';
import { useNotification } from '@/contexts/NotificationContext';

const AtmPage: React.FC = () => {
  const navigate = useNavigate();
  const { roles } = useAuthData();
  const hasAdminRole = roles.some((x) => x.name === 'Admin');
  const { data: atmsData } = useAtms();
  const { data: locationsData } = useLocations();
  const updateAtmAssetMutation = useUpdateAtmAsset();
  const createAtmAssetMutation = useCreateAtmAsset();
  const { showSuccess, showError } = useNotification();

  const [showInactive, setShowInactive] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<any>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newAtmAssetId, setNewAtmAssetId] = useState('');
  const [newAtmLocationId, setNewAtmLocationId] = useState<number | ''>('');

  const allAtms = useMemo(() => {
    if (!atmsData) return [];
    return Array.isArray(atmsData) ? atmsData : Object.values(atmsData);
  }, [atmsData]);

  // Filter out inactive assets by default
  const atms = useMemo(() => {
    if (showInactive) {
      return allAtms;
    }
    return allAtms.filter((atm: any) => atm.active !== false);
  }, [allAtms, showInactive]);

  const locations = useMemo(() => {
    if (!locationsData) return [];
    const locs = Array.isArray(locationsData) ? locationsData : Object.values(locationsData);
    return orderBy(locs, ['name'], ['asc']);
  }, [locationsData]);
  // Generate Location Filter Options (value as number for edit select, string for filter)
  const locationOptions = locations.map((location: any) => ({
    value: location.id.toString(),
    label: location.name,
  }));
  const locationEditOptions = locations.map((location: any) => ({
    value: location.id,
    label: location.name,
  }));

  const handleDelete = (atm: any) => {
    setAssetToDelete(atm);
    setDeleteConfirmOpen(true);
  };

  const handleReactivate = (atm: any) => {
    setAssetToDelete(atm);
    setDeleteConfirmOpen(true);
  };

  const handleAddAtm = () => {
    const assetId = (newAtmAssetId || '').trim();
    const locationId = newAtmLocationId === '' ? undefined : Number(newAtmLocationId);
    if (!assetId) {
      showError('ATM ID is required');
      return;
    }
    if (locationId == null) {
      showError('Location is required');
      return;
    }
    createAtmAssetMutation.mutate(
      { assetId, locationId },
      {
        onSuccess: () => {
          showSuccess('ATM added successfully');
          setAddDialogOpen(false);
          setNewAtmAssetId('');
          setNewAtmLocationId('');
        },
        onError: (error: any) => {
          showError(error.message || 'Failed to add ATM');
        },
      }
    );
  };

  const confirmDelete = () => {
    if (assetToDelete) {
      const isReactivating = assetToDelete.active === false || assetToDelete.active === null;
      const newActiveValue = isReactivating ? true : false;
      
      console.log('Updating ATM asset:', {
        id: assetToDelete.id,
        currentActive: assetToDelete.active,
        isReactivating,
        newActiveValue
      });
      
      updateAtmAssetMutation.mutate({ id: assetToDelete.id, active: newActiveValue }, {
        onSuccess: () => {
          setDeleteConfirmOpen(false);
          setAssetToDelete(null);
          showSuccess(isReactivating ? 'ATM reactivated successfully' : 'ATM deactivated successfully');
        },
        onError: (error: any) => {
          showError(error.message || 'Failed to update ATM');
        },
      });
    }
  };


  // Column visibility state with localStorage persistence
  const getStoredColumnVisibility = () => {
    const stored = localStorage.getItem('atmPage_columnVisibility');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const saveColumnVisibility = (visibility: Record<string, boolean>) => {
    localStorage.setItem('atmPage_columnVisibility', JSON.stringify(visibility));
  };

  const defaultColumnVisibility = {
    assetId: true,
    locationId: true,
    active: false,
    id: false,
    position: false,
    createdAt: false,
    updatedAt: false,
  };

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    const stored = getStoredColumnVisibility();
    return stored || defaultColumnVisibility;
  });

  const handleSaveRow = async ({ exitEditingMode, row, values }: any) => {
    const atm = row.original;
    const updatedAssetId = values.assetId;
    const updatedLocationId = values.locationId != null ? Number(values.locationId) : undefined;

    const payload: { id: number; assetId?: string; locationId?: number } = { id: atm.id };
    if (updatedAssetId !== atm.assetId) payload.assetId = updatedAssetId;
    if (updatedLocationId !== undefined && updatedLocationId !== atm.locationId) payload.locationId = updatedLocationId;

    if (payload.assetId !== undefined || payload.locationId !== undefined) {
      updateAtmAssetMutation.mutate(payload, {
        onSuccess: () => {
          exitEditingMode();
          showSuccess('ATM updated successfully');
        },
        onError: (error: any) => {
          showError(error.message || 'Failed to update ATM');
        },
      });
    } else {
      exitEditingMode();
    }
  };

  const atmColumns = [
    { 
      accessorKey: 'assetId', 
      header: 'ATM ID',
      enableEditing: hasAdminRole,
      Cell: ({ row }: any) => {
        return <span style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }} onClick={() => navigate(`/app/atm/${row.original.id}`)}>{row.original.assetId || row.original.id}</span>
      },
    },
    {
      accessorKey: 'locationId',
      header: 'Location',
      enableEditing: hasAdminRole,
      editVariant: 'select',
      editSelectOptions: locationEditOptions,
      Cell: ({ row }: any) => {
        const location = locations.find((loc: any) => loc.id === row.original.locationId);
        return <span style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }} onClick={() => navigate(`/app/locations/${row.original.locationId}`)}>{location?.name || 'No name'}</span>
      },
      filterVariant: 'select',
      filterSelectOptions: locationOptions,
    },
    {
      accessorKey: 'active', header: 'Active',
      Cell: ({ row }: any) => {
        return row.original.active !== false ? 'Yes' : 'No';
      },
      enableHiding: true,
    },
    {
      accessorKey: 'position', header: 'Position',
      Cell: ({ row }: any) => row.original.position || '-',
      enableHiding: true,
    },
    {
      accessorKey: 'id',
      header: 'ID',
      Cell: ({ row }: any) => row.original.id,
      enableHiding: true,
      enableEditing: false,
      muiTableBodyCellProps: ({ row, table }: any) => {
        const isEditing = table.getState().editingRow?.id === row.id;
        return {
          sx: isEditing ? { opacity: 0.6, backgroundColor: 'action.hover' } : undefined,
        };
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      Cell: ({ row }: any) => row.original.createdAt ? new Date(row.original.createdAt).toLocaleDateString() : '-',
      enableHiding: true,
      enableEditing: false,
      muiTableBodyCellProps: ({ row, table }: any) => {
        const isEditing = table.getState().editingRow?.id === row.id;
        return {
          sx: isEditing ? { opacity: 0.6, backgroundColor: 'action.hover' } : undefined,
        };
      },
    },
    {
      accessorKey: 'updatedAt',
      header: 'Updated At',
      Cell: ({ row }: any) => row.original.updatedAt ? new Date(row.original.updatedAt).toLocaleDateString() : '-',
      enableHiding: true,
      enableEditing: false,
      muiTableBodyCellProps: ({ row, table }: any) => {
        const isEditing = table.getState().editingRow?.id === row.id;
        return {
          sx: isEditing ? { opacity: 0.6, backgroundColor: 'action.hover' } : undefined,
        };
      },
    },
  ];

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'ATMs' }, // No href for the current page
  ];

  const rightContent = (
    <>
      <Button
        variant="outlined"
        startIcon={<Download />}
        sx={{ textTransform: 'none' }}
      >
        Export
      </Button>
      <Button
        onClick={() => hasAdminRole && setAddDialogOpen(true)}
        variant="contained"
        sx={{ textTransform: 'none' }}
        disabled={!hasAdminRole}
      >
        + Add ATM
      </Button>
    </>
  );


  return (
    <>
      <TitleBar
        breadcrumbs={breadcrumbs}
        title="ATMs"
        rightContent={rightContent}
      />

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <FormControlLabel
          control={
            <Switch
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
          }
          label="Show Inactive"
        />
      </Box>

      {/* Tables */}
      <MaterialReactTable 
        columns={atmColumns as any} 
        data={atms} 
        initialState={{ 
          density: 'compact',
          columnVisibility: columnVisibility
        }}
        state={{
          columnVisibility: columnVisibility
        }}
        onColumnVisibilityChange={(updater) => {
          const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
          setColumnVisibility(newVisibility);
          saveColumnVisibility(newVisibility);
        }}
        enableEditing={hasAdminRole}
        onEditingRowSave={handleSaveRow}
        enableRowActions={hasAdminRole}
        renderRowActions={({ row, table }) => (
          <Box sx={{ display: 'flex', gap: '1rem' }}>
            <IconButton
              onClick={() => table.setEditingRow(row)}
              size="small"
              title="Edit"
            >
              <Edit />
            </IconButton>
            {row.original.active === false || row.original.active === null ? (
              <IconButton
                size="small"
                onClick={() => handleReactivate(row.original)}
                color="primary"
                title="Reactivate"
              >
                <Restore />
              </IconButton>
            ) : (
              <IconButton
                size="small"
                onClick={() => handleDelete(row.original)}
                color="error"
                title="Delete"
              >
                <Delete />
              </IconButton>
            )}
          </Box>
        )}
      />

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>
          {(assetToDelete?.active === false || assetToDelete?.active === null) ? 'Confirm Reactivate' : 'Confirm Delete'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {(assetToDelete?.active === false || assetToDelete?.active === null) ? (
              <>Are you sure you want to reactivate ATM <strong>{assetToDelete?.assetId || assetToDelete?.id}</strong>?</>
            ) : (
              <>Are you sure you want to delete ATM <strong>{assetToDelete?.assetId || assetToDelete?.id}</strong>? 
              This will set it as inactive.</>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button 
            onClick={confirmDelete} 
            variant="contained" 
            color={(assetToDelete?.active === false || assetToDelete?.active === null) ? "primary" : "error"}
          >
            {(assetToDelete?.active === false || assetToDelete?.active === null) ? 'Reactivate' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { sx: { minWidth: 320, minHeight: 200 } },
        }}
      >
        <DialogTitle>Add ATM</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, overflow: 'visible' }}>
          <TextField
            label="ATM Asset ID *"
            value={newAtmAssetId}
            onChange={(e) => setNewAtmAssetId(e.target.value)}
            placeholder="e.g. ATM-01"
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />
          <FormControl fullWidth required sx={{ minWidth: 0 }}>
            <InputLabel id="add-atm-location-label" shrink>Location *</InputLabel>
            <Select
              labelId="add-atm-location-label"
              label="Location"
              value={newAtmLocationId}
              onChange={(e) => setNewAtmLocationId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <MenuItem value="">
                <em>Select location</em>
              </MenuItem>
              {locations.map((loc: any) => (
                <MenuItem key={loc.id} value={loc.id}>
                  {loc.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddAtm}
            variant="contained"
            disabled={createAtmAssetMutation.isPending || !newAtmAssetId.trim() || newAtmLocationId === ''}
          >
            {createAtmAssetMutation.isPending ? 'Adding…' : 'Add ATM'}
          </Button>
        </DialogActions>
      </Dialog>
    </>

  );
};

export default AtmPage;
